import { KycOrgData } from "./KycOrgForm.schema";
import { KycOrgMode } from "./KycOrgForm.types";

/** Map clean schema data -> contact.cgProfiling key names. */
export function flattenKycOrg(
  data: KycOrgData,
  mode: KycOrgMode = "combined"
): Record<string, unknown> {
  const zipCity = (zip: string, city: string) =>
    [zip, city].filter(Boolean).join(" ");

  const resolveOther = (val: string, custom: string | undefined) =>
    val === "other" ? custom ?? "" : val;

  /**
   * For contact.cgProfiling.* profile fields — stores "CUSTOM-<text>" so the
   * form can distinguish a custom entry from a standard option on reload.
   */
  const resolveProfileOther = (val: string, custom: string | undefined) =>
    val === "other" ? `CUSTOM-${custom ?? ""}` : val;

  const base: Record<string, unknown> = {
    // -- Selskapsdata (contact model fields)
    conName: data.orgName,
    conId: data.orgNumber,
    conAddress: data.address,
    conZipCity: zipCity(data.zip, data.city),
    conCountry: data.taxCountry,
    conPhone: data.phone,
    conMail: data.email,

    // -- Daglig kontakt (contact model fields)
    mcName: data.mcName,
    mcId: data.mcId,
    mcAddress: data.mcAddress,
    mcZipCity: zipCity(data.mcZip ?? "", data.mcCity ?? ""),
    mcEmail: data.mcEmail,
  };

  // -- KYC fields
  if (mode !== "mifid") {
    Object.assign(base, {
      "contact.cgProfiling.amlContactPerson": data.mcName,
      "contact.cgProfiling.amlContactPersonPowerOfAttorney":
        data.amlContactPersonPowerOfAttorney,
      "contact.cgProfiling.amlContactPersonPep": data.amlContactPersonPep,

      // Reelle rettighetshavere
      "contact.cgProfiling.amlBeneficialOwner1Ssn":
        data.amlBeneficialOwner1.ssn,
      "contact.cgProfiling.amlBeneficialOwner1Name":
        data.amlBeneficialOwner1.name,
      "contact.cgProfiling.amlBeneficialOwner1Ownership":
        data.amlBeneficialOwner1.ownership,
      "contact.cgProfiling.amlBeneficialOwner1VotingRights":
        data.amlBeneficialOwner1.votingRights,
      "contact.cgProfiling.amlBeneficialOwner1Pep":
        data.amlBeneficialOwner1.pep,
      "contact.cgProfiling.amlBeneficialOwner2Ssn":
        data.amlBeneficialOwner2.ssn,
      "contact.cgProfiling.amlBeneficialOwner2Name":
        data.amlBeneficialOwner2.name,
      "contact.cgProfiling.amlBeneficialOwner2Ownership":
        data.amlBeneficialOwner2.ownership,
      "contact.cgProfiling.amlBeneficialOwner2VotingRights":
        data.amlBeneficialOwner2.votingRights,
      "contact.cgProfiling.amlBeneficialOwner2Pep":
        data.amlBeneficialOwner2.pep,
      "contact.cgProfiling.amlBeneficialOwner3Ssn":
        data.amlBeneficialOwner3.ssn,
      "contact.cgProfiling.amlBeneficialOwner3Name":
        data.amlBeneficialOwner3.name,
      "contact.cgProfiling.amlBeneficialOwner3Ownership":
        data.amlBeneficialOwner3.ownership,
      "contact.cgProfiling.amlBeneficialOwner3VotingRights":
        data.amlBeneficialOwner3.votingRights,
      "contact.cgProfiling.amlBeneficialOwner3Pep":
        data.amlBeneficialOwner3.pep,
      "contact.cgProfiling.amlBeneficialOwner4Ssn":
        data.amlBeneficialOwner4.ssn,
      "contact.cgProfiling.amlBeneficialOwner4Name":
        data.amlBeneficialOwner4.name,
      "contact.cgProfiling.amlBeneficialOwner4Ownership":
        data.amlBeneficialOwner4.ownership,
      "contact.cgProfiling.amlBeneficialOwner4VotingRights":
        data.amlBeneficialOwner4.votingRights,
      "contact.cgProfiling.amlBeneficialOwner4Pep":
        data.amlBeneficialOwner4.pep,
      "contact.cgProfiling.amlBeneficialOwner5Ssn":
        data.amlBeneficialOwner5.ssn,
      "contact.cgProfiling.amlBeneficialOwner5Name":
        data.amlBeneficialOwner5.name,
      "contact.cgProfiling.amlBeneficialOwner5Ownership":
        data.amlBeneficialOwner5.ownership,
      "contact.cgProfiling.amlBeneficialOwner5VotingRights":
        data.amlBeneficialOwner5.votingRights,
      "contact.cgProfiling.amlBeneficialOwner5Pep":
        data.amlBeneficialOwner5.pep,

      // AML sporsmal
      "contact.cgProfiling.amlBusinessActivity": resolveProfileOther(
        data.amlBusinessActivity,
        data.amlBusinessActivityCustom
      ),
      "contact.cgProfiling.amlPurposeOfCustomerRelationship":
        resolveProfileOther(
          data.amlPurposeOfCustomerRelationship,
          data.amlPurposeOfCustomerRelationshipCustom
        ),
      "contact.cgProfiling.amlInvestedPortfolio": data.amlInvestedPortfolio,
      "contact.cgProfiling.amlAnnualDeposits": data.amlAnnualDeposits,
      "contact.cgProfiling.amlSourceOfFunds": resolveProfileOther(
        data.amlSourceOfFunds,
        data.amlSourceOfFundsCustom
      ),
      "contact.cgProfiling.amlSourceOfWealth": data.amlSourceOfWealth,
      "contact.cgProfiling.amlManagedOnBehalfOfOthers":
        data.amlManagedOnBehalfOfOthers,
      "contact.cgProfiling.amlTransfersAbroad": resolveProfileOther(
        data.amlTransfersAbroad,
        data.amlTransfersAbroadCustom
      ),
      "contact.cgProfiling.amlCryptoCurrency": data.amlCryptoCurrency,
    });
  }

  // -- MIFID fields
  if (mode !== "kyc") {
    Object.assign(base, {
      "contact.cgProfiling.mifidKnowledgeOfMutualFunds":
        data.mifidKnowledgeOfMutualFunds,
      "contact.cgProfiling.mifidExperienceOfMutualFunds":
        data.mifidExperienceOfMutualFunds,
      "contact.cgProfiling.mifidTradingFrequencyOfMutualFunds":
        data.mifidTradingFrequencyOfMutualFunds,
      "contact.cgProfiling.mifidPeriodOfMutualFunds":
        data.mifidPeriodOfMutualFunds,
      "contact.cgProfiling.mifidKnowledgeOfListedStocks":
        data.mifidKnowledgeOfListedStocks,
      "contact.cgProfiling.mifidExperienceOfListedStocks":
        data.mifidExperienceOfListedStocks,
      "contact.cgProfiling.mifidTradingFrequencyOfListedStocks":
        data.mifidTradingFrequencyOfListedStocks,
      "contact.cgProfiling.mifidPeriodOfListedStocks":
        data.mifidPeriodOfListedStocks,
      "contact.cgProfiling.mifidKnowledgeOfUnlistedStocks":
        data.mifidKnowledgeOfUnlistedStocks,
      "contact.cgProfiling.mifidExperienceOfUnlistedStocks":
        data.mifidExperienceOfUnlistedStocks,
      "contact.cgProfiling.mifidTradingFrequencyOfUnlistedStocks":
        data.mifidTradingFrequencyOfUnlistedStocks,
      "contact.cgProfiling.mifidPeriodOfUnlistedStocks":
        data.mifidPeriodOfUnlistedStocks,
      "contact.cgProfiling.mifidKnowledgeOfBonds": data.mifidKnowledgeOfBonds,
      "contact.cgProfiling.mifidExperienceOfBonds": data.mifidExperienceOfBonds,
      "contact.cgProfiling.mifidTradingFrequencyOfBonds":
        data.mifidTradingFrequencyOfBonds,
      "contact.cgProfiling.mifidPeriodOfBonds": data.mifidPeriodOfBonds,
      "contact.cgProfiling.mifidKnowledgeOfComplexInvestments":
        data.mifidKnowledgeOfComplexInvestments,
      "contact.cgProfiling.mifidExperienceOfComplexInvestments":
        data.mifidExperienceOfComplexInvestments,
      "contact.cgProfiling.mifidTradingFrequencyOfComplexInvestments":
        data.mifidTradingFrequencyOfComplexInvestments,
      "contact.cgProfiling.mifidPeriodOfComplexInvestments":
        data.mifidPeriodOfComplexInvestments,
      "contact.cgProfiling.mifidRelevantExperienceEducation":
        data.mifidRelevantExperienceEducation,

      "contact.cgProfiling.mifidTotalNetWealth": data.mifidTotalNetWealth,
      "contact.cgProfiling.mifidLiquidInvestableWealth":
        data.mifidLiquidInvestableWealth,
      "contact.cgProfiling.mifidGrossAnnualIncome": data.mifidGrossAnnualIncome,
      "contact.cgProfiling.mifidLiquidityBuffer": data.mifidLiquidityBuffer,
      "contact.cgProfiling.mifidDebtBurden": data.mifidDebtBurden,
      "contact.cgProfiling.mifidIncomeStability": data.mifidIncomeStability,
      "contact.cgProfiling.mifidEarliestWithdrawal":
        data.mifidEarliestWithdrawal,

      "contact.cgProfiling.mifidMainInvestmentPurpose":
        data.mifidMainInvestmentPurpose,
      "contact.cgProfiling.mifidRiskToleranceVolatility":
        data.mifidRiskToleranceVolatility,
      "contact.cgProfiling.mifidLiquidityNeed": data.mifidLiquidityNeed,
      "contact.cgProfiling.mifidMarketDropResponse":
        data.mifidMarketDropResponse,
      "contact.cgProfiling.mifidValueDeclineImpact":
        data.mifidValueDeclineImpact,

      "contact.cgProfiling.mifidEsgPreference": data.mifidEsgPreference,
      ...(data.mifidEsgPreference === "Ja" && {
        "contact.cgProfiling.mifidEsgPreferenceType":
          data.mifidEsgPreferenceType,
      }),
    });
  }

  return base;
}
